import _ from 'lodash';
import dvaModelExtend from 'dva-model-extend';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import { toPaginate } from '@/utils/infrastructure';

import { DEFAULT_ORDER_BY, ENTITY_NAME, FETCH_ROUTE, MODEL_NAME } from './constants';
import {
  decorateSubscription,
  normalizeCatalogue,
  normalizeCollection,
  slicePage,
} from './derived';
import { notifyGlobal, reportWriteError } from './effects';

const api = bindServices(MODEL_NAME);

const _model = createCrudModel({
  namespace: MODEL_NAME,
  fetchRoute: FETCH_ROUTE,
  fetchParams: 'SecurityEvents',
  entityName: ENTITY_NAME,
  api,
  features: {
    import: false,
    bulkActions: false,
    repeatableLatestFetch: 'fetchList',
  },
  state: {
    showDestinationsDrawer: false,
    drawerSubscription: null,
    operationalView: null,
    deleteConfirm: null,
    destinationRows: [],
    environmentNames: {},
    catalogue: normalizeCatalogue(),
  },
  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const limit = payload?.limit ?? pageSize;
        const page = payload?.page ?? 1;
        const model = yield select((state) => state[MODEL_NAME]);
        yield put({ type: 'queryLatestFetchPayload', payload: payload || {} });

        // `$skip` is not part of this list contract. Ask for the company array
        // and paginate in the client so ConfigListPage can keep its pager.
        const response = yield call(api.getAll, {
          route: FETCH_ROUTE,
          params: {
            select: payload?.select || model.select,
            search: payload?.search,
            searchSelect: model.searchSelect,
            filter: payload?.filter,
            orderBy: payload?.orderBy || DEFAULT_ORDER_BY,
          },
          readPriority: true,
        });

        let destinations = [];
        try {
          const destinationResponse = yield call(api.getAll, {
            route: `${FETCH_ROUTE}/destinations`,
            params: {
              select: 'id,subscriptionId,isActive,status',
              orderBy: 'name asc',
            },
            readPriority: true,
          });
          destinations = normalizeCollection(destinationResponse);
        } catch (e) {
          destinations = [];
        }

        const all = normalizeCollection(response).map((item) =>
          decorateSubscription(item, destinations),
        );
        yield put({
          type: 'querySubscriptionData',
          payload: {
            items: slicePage(all, page, limit),
            totalCount: all.length,
            page,
            limit,
            destinationRows: destinations,
          },
        });
      } catch (e) {
        /* list errors surface through the shared request handler */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const created = yield call(api.create, { route: FETCH_ROUTE, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield* notifyGlobal(put, { type: 'success' });
        if (created?.id) {
          yield put({
            type: 'queryDestinationsDrawer',
            payload: { open: true, item: created },
          });
        }
      } catch (e) {
        yield* reportWriteError(e, put);
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield call(api.remove, { route: FETCH_ROUTE, id });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield put({ type: 'queryDeleteConfirm', payload: null });
        yield put({ type: 'success', payload: { success: true } });
        yield* notifyGlobal(put, { type: 'success' });
      } catch (e) {
        if (e?.message) yield* notifyGlobal(put, { type: 'error', message: e.message });
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    },

    *showDestinationsDrawer({ payload }, { put }) {
      yield put({ type: 'queryDestinationsDrawer', payload });
    },

    *showOperationalStatus({ payload }, { put }) {
      yield put({ type: 'queryOperationalView', payload });
    },

    *openDeleteConfirm({ payload }, { put }) {
      yield put({ type: 'queryDeleteConfirm', payload });
    },

    *closeDeleteConfirm(_, { put }) {
      yield put({ type: 'queryDeleteConfirm', payload: null });
    },

    *fetchCatalogue(_, { call, put }) {
      try {
        const catalogueResponse = yield call(api.getAll, {
          route: `${FETCH_ROUTE}/catalogue`,
          readPriority: true,
        });
        yield put({
          type: 'queryCatalogue',
          payload: normalizeCatalogue(normalizeCollection(catalogueResponse)),
        });
      } catch (e) {
        yield put({ type: 'queryCatalogue', payload: normalizeCatalogue() });
      }
    },

    *fetchEnvironmentName({ payload }, { call, put }) {
      const id = payload?.id;
      if (!id) return;
      let name = null;
      try {
        const response = yield call(api.getAll, {
          route: 'environment/paged',
          params: {
            select: 'id,name',
            filter: `id eq ${id}`,
            page: 1,
            limit: 1,
          },
          readPriority: true,
        });
        name = normalizeCollection(response).find((row) => row?.id === id)?.name || null;
      } catch (e) {
        name = null;
      }
      yield put({ type: 'queryEnvironmentName', payload: { id, name } });
    },
  },
  reducers: {
    querySubscriptionData(state, { payload }) {
      return {
        ...state,
        list: payload.items || [],
        pagination: _.merge({}, state.pagination, toPaginate(payload)),
        destinationRows: payload.destinationRows || [],
      };
    },
    queryCatalogue(state, { payload }) {
      return { ...state, catalogue: payload?.length ? payload : state.catalogue };
    },
    queryEnvironmentName(state, { payload }) {
      if (!payload?.id) return state;
      const environmentNames = { ...state.environmentNames, [payload.id]: payload.name };
      const item =
        payload.name && state.item?.scope?.id === payload.id
          ? { ...state.item, environmentName: payload.name }
          : state.item;
      return { ...state, environmentNames, item };
    },
    securityEventActivate(state, { payload }) {
      const list = state.list.map((item) => {
        if (item.id !== payload.id) return item;
        return decorateSubscription(
          { ...item, isActive: payload.state === true },
          state.destinationRows,
          state.environmentNames,
        );
      });
      return { ...state, list };
    },
    syncSubscriptionDestinations(state, { payload }) {
      const subscriptionId = payload?.subscriptionId;
      if (!subscriptionId) return state;
      const incoming = (payload.rows || []).map((row) => ({
        id: row?.id,
        subscriptionId,
        isActive: row?.isActive === true,
        status: row?.status,
      }));
      const destinationRows = [
        ...(state.destinationRows || []).filter((row) => row?.subscriptionId !== subscriptionId),
        ...incoming,
      ];
      return {
        ...state,
        destinationRows,
        list: (state.list || []).map((item) =>
          decorateSubscription(item, destinationRows, state.environmentNames),
        ),
      };
    },
    patchDestinationActive(state, { payload }) {
      if (!payload?.id) return state;
      const destinationRows = (state.destinationRows || []).map((row) =>
        row.id === payload.id ? { ...row, isActive: payload.isActive === true } : row,
      );
      return {
        ...state,
        destinationRows,
        list: (state.list || []).map((item) =>
          decorateSubscription(item, destinationRows, state.environmentNames),
        ),
      };
    },
    queryDestinationsDrawer(state, { payload }) {
      return {
        ...state,
        showDestinationsDrawer: !!payload?.open,
        drawerSubscription: payload?.open ? payload.item ?? null : null,
      };
    },
    queryOperationalView(state, { payload }) {
      if (!payload?.open || !payload?.subscriptionId) {
        return { ...state, operationalView: null };
      }
      return {
        ...state,
        operationalView: {
          subscriptionId: payload.subscriptionId,
          subscriptionName: payload.subscriptionName || '',
          destinationId: payload.destinationId || null,
          destinationName: payload.destinationName || '',
          fromDestination: payload.fromDestination === true,
        },
      };
    },
    queryDeleteConfirm(state, { payload }) {
      return {
        ...state,
        deleteConfirm: payload?.id ? payload : null,
      };
    },
    queryLatestFetchPayload(state, { payload }) {
      return { ...state, latestFetchPayload: payload || null };
    },
  },
});

export default dvaModelExtend(_model, { namespace: MODEL_NAME });
